import { Button, Fieldset, Group, NumberInput, Stack } from "@mantine/core";
import { useEffect, useState } from "react";

import {
  type NullableNumberRange,
  parseNumberInputValue,
} from "./numberInput.js";
import { useDebouncedRangeCommit } from "./useDebouncedRangeCommit.js";

export interface MetricRangeFilterProps {
  readonly label: "ROAS" | "Spend";
  readonly onChange: (value: NullableNumberRange) => boolean | void;
  readonly value: NullableNumberRange;
}

interface NumberInputDraft {
  readonly resetGeneration: number;
  readonly value: string | number;
}

function numberInputDraft(value: number | null): NumberInputDraft {
  return { resetGeneration: 0, value: value ?? "" };
}

export function MetricRangeFilter({
  label,
  onChange,
  value,
}: MetricRangeFilterProps) {
  const accessibleLabel = label === "ROAS" ? label : label.toLocaleLowerCase("en-US");
  const [minimumDraft, setMinimumDraft] = useState(() => numberInputDraft(value.min));
  const [maximumDraft, setMaximumDraft] = useState(() => numberInputDraft(value.max));
  const resetDrafts = () => {
    setMinimumDraft((current) => ({
      resetGeneration: current.resetGeneration + 1,
      value: value.min ?? "",
    }));
    setMaximumDraft((current) => ({
      resetGeneration: current.resetGeneration + 1,
      value: value.max ?? "",
    }));
  };
  const commit = useDebouncedRangeCommit({ onChange, onRejected: resetDrafts, value });

  useEffect(() => {
    setMinimumDraft((current) => ({ ...current, value: value.min ?? "" }));
    setMaximumDraft((current) => ({ ...current, value: value.max ?? "" }));
  }, [value.max, value.min]);

  const restoreMinimumIfIncomplete = () => {
    const draft = minimumDraft.value;
    if (draft === "" || parseNumberInputValue(draft) !== null) commit.flush();
    else {
      commit.cancel();
      setMinimumDraft((current) => ({ ...current, value: value.min ?? "" }));
    }
  };
  const restoreMaximumIfIncomplete = () => {
    const draft = maximumDraft.value;
    if (draft === "" || parseNumberInputValue(draft) !== null) commit.flush();
    else {
      commit.cancel();
      setMaximumDraft((current) => ({ ...current, value: value.max ?? "" }));
    }
  };

  return (
    <Fieldset legend={label}>
      <Stack gap="sm">
        <Group align="end" grow>
          <NumberInput
            allowDecimal
            allowNegative={false}
            aria-label={`Minimum ${accessibleLabel}`}
            clampBehavior="strict"
            key={`minimum-${minimumDraft.resetGeneration}`}
            label="Minimum"
            max={value.max ?? undefined}
            min={0}
            onChange={(next) => {
              const parsed = parseNumberInputValue(next);
              if (next === "" || parsed !== null) {
                setMinimumDraft((current) => ({ ...current, value: next }));
                commit.schedule({ ...value, min: parsed });
              } else {
                commit.cancel();
                setMinimumDraft((current) => ({ ...current, value: next }));
              }
            }}
            onBlur={restoreMinimumIfIncomplete}
            onKeyDown={(event) => {
              if (event.key === "Enter") commit.flush();
            }}
            value={minimumDraft.value}
          />
          <NumberInput
            allowDecimal
            allowNegative={false}
            aria-label={`Maximum ${accessibleLabel}`}
            clampBehavior="strict"
            key={`maximum-${maximumDraft.resetGeneration}`}
            label="Maximum"
            min={value.min ?? 0}
            onChange={(next) => {
              const parsed = parseNumberInputValue(next);
              if (next === "" || parsed !== null) {
                setMaximumDraft((current) => ({ ...current, value: next }));
                commit.schedule({ ...value, max: parsed });
              } else {
                commit.cancel();
                setMaximumDraft((current) => ({ ...current, value: next }));
              }
            }}
            onBlur={restoreMaximumIfIncomplete}
            onKeyDown={(event) => {
              if (event.key === "Enter") commit.flush();
            }}
            value={maximumDraft.value}
          />
        </Group>
        <Button
          disabled={value.min === null && value.max === null}
          onClick={() => {
            commit.cancel();
            const accepted = onChange({ max: null, min: null });
            setMaximumDraft((current) => ({
              resetGeneration: current.resetGeneration + (accepted === false ? 1 : 0),
              value: accepted === false ? value.max ?? "" : "",
            }));
            setMinimumDraft((current) => ({
              resetGeneration: current.resetGeneration + (accepted === false ? 1 : 0),
              value: accepted === false ? value.min ?? "" : "",
            }));
          }}
          size="compact-sm"
          variant="subtle"
        >
          Clear {label}
        </Button>
      </Stack>
    </Fieldset>
  );
}
